<svelte:options runes={false} />
<script>
  // Realized-slippage-over-time chart (TASK-20260728-researcher-observability-ui, §10): one dot per terminal
  // order (side-adjusted bps; positive = adverse), a zero baseline, and the MODELED-cost reference line the
  // paper record exists to measure against. Hand-rolled Canvas 2D on a time x-scale (orders are irregular),
  // per the repo idiom. Sign is encoded by position around the zero line AND named in the hover readout —
  // never color-alone.
  import { onMount } from 'svelte';
  import { COLORS } from './chartUtils.js';
  import { xForTime, timeDomain } from '../../../lib/lineage.js';

  export let points = [];      // [{ time, value }] ascending (computable samples only)
  export let modeledBps = 20;  // the reference line

  let canvas;
  let ctx;
  let container;
  let width = 600;
  const height = 220;
  const PAD = { top: 14, right: 56, bottom: 24, left: 10 };
  let hoverIdx = -1;

  $: hasData = (points?.length ?? 0) > 0;
  $: domain = timeDomain([points]);

  function bounds() {
    const vs = points.map((p) => p.value);
    let lo = Math.min(0, ...vs);
    let hi = Math.max(modeledBps, ...vs);
    const pad = (hi - lo) * 0.12 || 1;
    return { lo: lo - pad, hi: hi + pad };
  }

  function draw() {
    if (!ctx || !hasData) return;
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, width, height);

    const { lo, hi } = bounds();
    const plotW = width - PAD.left - PAD.right;
    const plotH = height - PAD.top - PAD.bottom;
    const yOf = (v) => PAD.top + (1 - (v - lo) / (hi - lo)) * plotH;
    const xOf = (t) => xForTime(Date.parse(t), domain, PAD.left, plotW);

    // Grid + right labels.
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'right';
    for (let i = 0; i <= 4; i++) {
      const v = hi - ((hi - lo) * i) / 4;
      const y = yOf(v);
      ctx.strokeStyle = COLORS.grid;
      ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(width - PAD.right, y); ctx.stroke();
      ctx.fillStyle = COLORS.label;
      ctx.fillText(`${v.toFixed(0)} bps`, width - 5, y + 3);
    }

    // Zero baseline (favorable below, adverse above).
    const y0 = yOf(0);
    ctx.strokeStyle = COLORS.label;
    ctx.globalAlpha = 0.7;
    ctx.beginPath(); ctx.moveTo(PAD.left, y0); ctx.lineTo(width - PAD.right, y0); ctx.stroke();
    ctx.globalAlpha = 1;

    // Modeled reference line — labeled, dashed.
    const ym = yOf(modeledBps);
    ctx.strokeStyle = '#d8a000';
    ctx.setLineDash([5, 4]);
    ctx.beginPath(); ctx.moveTo(PAD.left, ym); ctx.lineTo(width - PAD.right, ym); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#d8a000';
    ctx.textAlign = 'left';
    ctx.fillText(`modeled ${modeledBps} bps`, PAD.left + 4, ym - 4);

    // One dot per order; adverse (>0) vs favorable (<=0) also differ by fill vs ring (not color-alone).
    points.forEach((p, i) => {
      const x = xOf(p.time);
      const y = yOf(p.value);
      const adverse = p.value > 0;
      ctx.fillStyle = adverse ? COLORS.loss : COLORS.gain;
      ctx.strokeStyle = ctx.fillStyle;
      ctx.beginPath();
      ctx.arc(x, y, i === hoverIdx ? 4.5 : 3, 0, Math.PI * 2);
      if (adverse) ctx.fill(); else { ctx.lineWidth = 1.5; ctx.stroke(); }
    });

    // Time labels.
    ctx.fillStyle = COLORS.label;
    ctx.textAlign = 'center';
    const span = domain.max - domain.min;
    if (span > 0) {
      for (let i = 0; i <= 5; i++) {
        const t = domain.min + (span * i) / 5;
        const d = new Date(t);
        ctx.fillText(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), PAD.left + (plotW * i) / 5, height - 6);
      }
    }
  }

  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const plotW = width - PAD.left - PAD.right;
    const { lo, hi } = bounds();
    const plotH = height - PAD.top - PAD.bottom;
    let best = -1;
    let bestDist = 12;
    points.forEach((p, i) => {
      const x = xForTime(Date.parse(p.time), domain, PAD.left, plotW);
      const y = PAD.top + (1 - (p.value - lo) / (hi - lo)) * plotH;
      const d = Math.hypot(x - mx, y - my);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    hoverIdx = best;
  }
  function onLeave() { hoverIdx = -1; }

  $: hovered = hoverIdx >= 0 ? points[hoverIdx] : null;
  $: if (ctx && canvas && hasData && width && (hoverIdx || true) && points && modeledBps != null) draw();
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  function resize() {
    if (!container) return;
    width = Math.max(280, container.offsetWidth);
  }

  onMount(() => {
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    if (ro && container) ro.observe(container);
    return () => { window.removeEventListener('resize', onResize); ro?.disconnect(); };
  });
</script>

<div class="sc-root" bind:this={container}>
  <h4 class="sc-title">Realized slippage vs modeled</h4>
  {#if hasData}
    <canvas bind:this={canvas} width={width} height={height} tabindex="0"
      aria-label={`Slippage per order: ${points.length} samples against the modeled ${modeledBps} bps line. Positive is adverse.`}
      on:mousemove={onMove} on:mouseleave={onLeave}></canvas>
    <div class="sc-hover" aria-live="polite">
      {#if hovered}
        {new Date(hovered.time).toLocaleString()} · {hovered.value > 0 ? 'adverse' : 'favorable'} {Math.abs(hovered.value).toFixed(1)} bps
      {:else}&nbsp;{/if}
    </div>
  {:else}
    <div class="empty-state"><p class="empty-state-description">No filled orders with computable slippage in this window yet.</p></div>
  {/if}
</div>

<style>
  .sc-root { width: 100%; }
  .sc-title { margin: 0 0 0.4rem; font-size: 0.8rem; color: var(--text-secondary, #b5b6b8); }
  canvas { display: block; width: 100%; border: 1px solid var(--border-subtle, #1e1f22); border-radius: 6px; }
  canvas:focus-visible { outline: 2px solid var(--accent, #3c78f0); outline-offset: 1px; }
  .sc-hover { min-height: 1.1rem; font-size: 0.75rem; color: var(--text-secondary, #b5b6b8); padding: 0.25rem 0; font-variant-numeric: tabular-nums; }
</style>
